// @ts-ignore
import css from "./widget.css";
import { openMenu } from "../menu/menu";
import { getCurrentDevice, DeviceType } from "@/utils/device";
import { Accessibility } from "@/icons/accessibility";
import { Assist } from "@/icons/assist";
import { DefaultIcon } from "@/icons/default";
import { wheel } from "@/icons/wheel";
import { pluginConfig } from "@/globals/pluginConfig";
import { IconItem } from "@/types/widget";
import { detectedLanguage } from "@/utils/getScriptDataAttribute";
import { LANGUAGE_DICTIONARY } from "@/i18n/Languages";
import { getDefaultLanguage } from "@/i18n/getDefaultLanguage";
import { getShortcutLabel } from '@/utils/getShortcutLabel';

/**
 * Icon library for the widget
 */
export const ICON_LIBRARY: readonly IconItem[] = [
  { id: "default", icon: DefaultIcon },
  { id: "accessibility", icon: Accessibility },
  { id: "assist", icon: Assist },
  { id: "wheel", icon: wheel },
] as const;

export let $widget: HTMLElement | null = null;

function hexToRgb(hex: string): [number, number, number] | null {
    const cleaned = hex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
    return [
        parseInt(cleaned.slice(0, 2), 16),
        parseInt(cleaned.slice(2, 4), 16),
        parseInt(cleaned.slice(4, 6), 16),
    ];
}

function getRelativeLuminance(r: number, g: number, b: number): number {
    const toLinear = (c: number) => {
        const s = c / 255;
        return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function getContrastRatio(hex1: string, hex2: string): number {
    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);
    if (!rgb1 || !rgb2) return 1;
    const l1 = getRelativeLuminance(...rgb1);
    const l2 = getRelativeLuminance(...rgb2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Position styles configuration
 */
const POSITION_STYLES = {
  "bottom-right": (margins: any) => ({
    bottom: `${margins.bottom}px`,
    right: `${margins.right}px`,
    left: "auto",
  }),
  "top-left": (margins: any) => ({
    top: `${margins.top}px`,
    left: `${margins.left}px`,
    bottom: "auto",
    right: "auto",
  }),
  "top-right": (margins: any) => ({
    top: `${margins.top}px`,
    right: `${margins.right}px`,
    left: "auto",
    bottom: "auto",
  }),
  "bottom-left": (margins: any) => ({
    bottom: `${margins.bottom}px`,
    left: `${margins.left}px`,
    right: "auto"
  }),
} as const;

/**
 * Gets button styles based on current device and position
 * @returns CSS styles object
 */
function getButtonStyle(): Record<string, string> {
  const device = getCurrentDevice();
  const { position, margins } = pluginConfig;
  const currentPosition = position[device];
  const currentMargins = margins[device];

  const positionStyle =
    POSITION_STYLES[currentPosition as keyof typeof POSITION_STYLES];
  return positionStyle
    ? positionStyle(currentMargins)
    : POSITION_STYLES["bottom-left"](currentMargins);
}

/**
 * Handles keyboard navigation for the widget button
 * @param event - Keyboard event
 * @param button - The button element
 */
function handleWidgetKeyboardNavigation(event: KeyboardEvent, button: HTMLElement): void {
  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault();
      openMenu();
      break;
    case 'Escape':
      // Close menu if open by finding it within the widget's shadow DOM
      const widgetContainer = document.querySelector('#cya11y-container');
      if (widgetContainer) {
        const shadowRoot = widgetContainer.shadowRoot;
        if (shadowRoot) {
          const menu = shadowRoot.querySelector('.cya11y-menu') as HTMLElement;
          if (menu && menu.style.display !== 'none') {
            const closeButton = menu.querySelector('.cya11y-menu-close') as HTMLElement;
            if (closeButton) {
              closeButton.click();
              // Update ARIA expanded state
              button.setAttribute('aria-expanded', 'false');
            }
          }
        }
      }
      break;
  }
}

/**
 * Renders the accessibility widget
 * @returns The widget element or null if not rendered
 */
export function renderWidget(): HTMLElement | null {
  try {
    const device = getCurrentDevice();

    // Check if widget should be displayed based on device status
    const deviceStatus =
      device === "mobile"
        ? pluginConfig.status.mobile
        : pluginConfig.status.desktop;

    if (!deviceStatus) {
      console.log(`Widget disabled for ${device} device`);
      return null;
    }

    // Create container and shadow DOM
    const $container: HTMLElement = document.createElement("div");
    $container.id = "cya11y-container";
    const shadow = $container.attachShadow({ mode: "open" });

    const currentPosition = pluginConfig.position[device];
    const selectedIcon = ICON_LIBRARY.find(
      (icon) => icon.id === pluginConfig.iconId
    );

    const defaultLanguage = getDefaultLanguage();
    // Create widget HTML with enhanced accessibility
    const baseLabel = LANGUAGE_DICTIONARY[defaultLanguage][pluginConfig.label] || 'Accessibility menu';
    const shortcutHint = pluginConfig.keyboard?.enabled && pluginConfig.keyboard.shortcut
        ? ` — Press ${getShortcutLabel(pluginConfig.keyboard.shortcut)} to open/close`
        : '';
    const buttonLabel = `${baseLabel}${shortcutHint}`;
    shadow.innerHTML = `
      <style id="cya11y-widget-css">${css}</style>
      <div class="cya11y-widget" data-position="${currentPosition}">
        <div class="cya11y-widget-icon">
          <button
             class="cya11y-menu-btn"
             aria-label="${buttonLabel}"
             title="${buttonLabel}"
             aria-expanded="false"
             aria-haspopup="dialog"
             role="button"
             type="button"
             tabindex="0">
            <span class="cya11y-btn-content" aria-hidden="true">
              ${selectedIcon?.icon || DefaultIcon}
            </span>
            <span class="cya11y-sr-only" aria-hidden="true">Open accessibility menu</span>
          </button>
        </div>
      </div>
    `;

    // Get widget element and apply styles
    $widget = shadow.querySelector(".cya11y-widget") as HTMLElement;
    if (!$widget) {
      throw new Error("Failed to find widget element");
    }

    $widget.style.setProperty("--cya11y-primary-color", pluginConfig.primaryColor);
    $widget.style.setProperty("--cya11y-size", `${pluginConfig.iconSize}px`);

    const contrastWithWhite = getContrastRatio(pluginConfig.primaryColor, '#ffffff');
    const primaryVisibleOnWhite = contrastWithWhite >= 3;
    $widget.style.setProperty('--cya11y-focus-color-on-primary', primaryVisibleOnWhite ? '#ffffff' : '#000000');
    $widget.style.setProperty('--cya11y-focus-color-on-white', primaryVisibleOnWhite ? pluginConfig.primaryColor : '#000000');
    $widget.style.setProperty('--cya11y-focus-ring-color', primaryVisibleOnWhite ? pluginConfig.primaryColor : '#000000');

    const rgb = hexToRgb(pluginConfig.primaryColor);
    const shadowColor = primaryVisibleOnWhite && rgb
        ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.14)`
        : 'rgba(0, 0, 0, 0.14)';
    $widget.style.setProperty('--cya11y-shadow-focus', `0px 0px 0px 6px ${shadowColor}`);

    const $btn = $widget.querySelector(".cya11y-menu-btn") as HTMLElement;
    const $icon = $widget.querySelector(".cya11y-widget-icon") as HTMLElement;

    if ($btn && $icon) {
      Object.assign($btn.style, getButtonStyle());
      Object.assign($icon.style, getButtonStyle());

      // Enhanced click event listener with accessibility improvements
      $btn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        openMenu();
        
        // Update ARIA expanded state after a short delay to ensure menu is rendered
        setTimeout(() => {
          const widgetContainer = document.querySelector('.cya11y-container');
          if (widgetContainer) {
            const widgetShadowRoot = widgetContainer.shadowRoot;
            if (widgetShadowRoot) {
              const menu = widgetShadowRoot.querySelector('.cya11y-menu') as HTMLElement;
              if (menu) {
                const isExpanded = menu.style.display !== 'none';
                $btn.setAttribute('aria-expanded', isExpanded.toString());
              }
            }
          }
        }, 10);
      });

      // Add keyboard navigation
      $btn.addEventListener("keydown", (event) => {
        handleWidgetKeyboardNavigation(event, $btn);
      });

      // Add focus management
      $btn.addEventListener("focus", () => {
        $btn.classList.add("cya11y-focus-visible");
      });

      $btn.addEventListener("blur", () => {
        $btn.classList.remove("cya11y-focus-visible");
      });
    }

    // Append to document
    document.body.insertBefore($container, document.body.firstChild);

    return $widget;
  } catch (error) {
    console.error("Failed to render widget:", error);
    return null;
  }
}
